// The tips on screen (src/help/tips.ts): a small card beside the control a tip is about, with a soft ring round the
// control, "1 of 3", Next and Skip. A first visit's tour starts a moment after a board of a kind the account has not
// had tips for settles, with nothing open over it; Skip, Esc or the last Next keeps the kind as seen on every device.
// It never stops the board: clicks go through to everything, the card included, and it never takes the focus from a
// field being typed in, nor covers what the field it points at shows under it (Fixes 9: the planner's inbox).

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { kindOf } from "../../shared/boardKinds";
import type { BoardKind } from "../../shared/types";
import { isOn, sawTips, useOnboarding } from "../state/onboarding";
import { currentBoard, get, useStore } from "../state/store";
import { endTour, quietForChecks, shownTips, useTour, type Tip } from "./tips";
import { useCardUp } from "./cards";
import "./chrome.css";

/** How long a board of a new kind is open before its tips start. */
const SETTLE_MS = 1600;
const GAP = 12;
const EDGE = 12;

export function Tips() {
  const kind = useStore((s) => kindOf(currentBoard(s)));
  const boardId = useStore((s) => s.boardId);
  // nothing over the board: no panel or dialog, not presenting, a board of the user's own, loaded
  const clear = useStore((s) => s.phase === "ready" && !s.guest && !!s.workspace && !s.panel && !s.presenting && !s.themePicker && !currentBoard(s)?.archived);
  // a corner card on screen (While you were away, What's new) goes first: the tips start once it is put away
  const card = useCardUp();
  const off = useOnboarding((o) => isOn(o.tipsOff));
  const seen = useOnboarding((o) => !!o.tips?.[kind]);
  const tour = useTour();
  useEffect(() => {
    if (!clear || card || off || seen || tour.kind || quietForChecks()) return;
    const t = setTimeout(() => {
      // the board drew its controls: a kind whose tips point at nothing on screen waits for its next visit
      // an empty board's first-time card up (one that can be put away) says what to do first: the tips wait for a visit
      // without it (Fixes 10); a card that is a step to take (a files board's Link your computer) never holds them back
      if (shownTips(kind).length && !document.querySelector('[data-ob="empty"] .ob-empty-x')) useTour.setState({ kind, step: 0, asked: false });
    }, SETTLE_MS);
    return () => clearTimeout(t);
  }, [clear, card, off, seen, kind, boardId, tour.kind]);
  // another board, of another kind, ends the tour on screen; a panel opened over it puts it away until it closes
  useEffect(() => {
    if (tour.kind && tour.kind !== kind) endTour();
  }, [kind, tour.kind]);
  if (!tour.kind || tour.kind !== kind || ((!clear || card) && !tour.asked) || (off && !tour.asked)) return null;
  return <TipCard kind={tour.kind} step={tour.step} asked={tour.asked} />;
}

/** Where the card goes beside its control: below, above, left or right, whichever fits, kept inside the window. */
function place(r: DOMRect, w: number, h: number): { left: number; top: number; side: "below" | "above" | "left" | "right" } {
  const vw = innerWidth;
  const vh = innerHeight;
  const clampX = (x: number) => Math.max(EDGE, Math.min(vw - w - EDGE, x));
  const clampY = (y: number) => Math.max(EDGE, Math.min(vh - h - EDGE, y));
  const cx = r.left + r.width / 2 - w / 2;
  const cy = r.top + r.height / 2 - h / 2;
  // a big area (a page, a grid): the card sits inside its top left, not beyond it
  if (r.width > vw * 0.4 && r.height > vh * 0.4) return { left: clampX(r.left + 24), top: clampY(r.top + 24), side: "below" };
  if (r.bottom + GAP + h <= vh - EDGE) return { left: clampX(cx), top: r.bottom + GAP, side: "below" };
  if (r.top - GAP - h >= EDGE) return { left: clampX(cx), top: r.top - GAP - h, side: "above" };
  if (r.left - GAP - w >= EDGE) return { left: r.left - GAP - w, top: clampY(cy), side: "left" };
  return { left: clampX(r.right + GAP), top: clampY(cy), side: "right" };
}

function TipCard({ kind, step, asked }: { kind: BoardKind; step: number; asked: boolean }) {
  const phone = useStore((s) => s.mobile);
  const card = useRef<HTMLDivElement>(null);
  const [list, setList] = useState(() => shownTips(kind));
  const [pos, setPos] = useState<{ left: number; top: number; side: string; ring: DOMRect } | null>(null);
  const at = Math.min(step, Math.max(0, list.length - 1));
  const tip: Tip | undefined = list[at]?.tip;

  const finish = () => {
    // a first visit's tour, seen or skipped, is kept for the account; one asked for again changes nothing kept
    if (!asked || !get().workspace?.onboarding?.tips?.[kind]) sawTips(kind);
    endTour();
  };
  const next = () => (at + 1 >= list.length ? finish() : useTour.setState({ step: at + 1 }));

  // the control may move (a panel, the chat, a scroll): the card follows it, measured at once on a scroll or a resize
  // and a few times a second besides, never every frame, so typing on the board keeps up (Fixes 9)
  useLayoutEffect(() => {
    let raf = 0;
    let beat: ReturnType<typeof setTimeout> | undefined;
    const soon = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(follow);
    };
    const follow = () => {
      const now = shownTips(kind);
      const same = now.length === list.length && now.every((x, i) => x.el === list[i]?.el);
      if (!same) setList(now);
      const el = (same ? list : now)[Math.min(at, now.length - 1)]?.el;
      const c = card.current;
      if (el && c) {
        const r = el.getBoundingClientRect();
        const p = phone ? { left: EDGE, top: innerHeight - c.offsetHeight - 92, side: "phone" } : place(r, c.offsetWidth, c.offsetHeight);
        setPos((was) => (was && was.left === p.left && was.top === p.top && was.ring.x === r.x && was.ring.y === r.y && was.ring.width === r.width && was.ring.height === r.height ? was : { ...p, ring: r }));
      }
      clearTimeout(beat);
      beat = setTimeout(soon, 160);
    };
    follow();
    window.addEventListener("scroll", soon, true);
    window.addEventListener("resize", soon);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(beat);
      window.removeEventListener("scroll", soon, true);
      window.removeEventListener("resize", soon);
    };
  }, [kind, at, list, phone]);

  // Esc skips, as Skip does; only while nothing else is open to take it
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || get().panel) return;
      e.stopPropagation();
      finish();
    };
    window.addEventListener("keydown", key, true);
    return () => window.removeEventListener("keydown", key, true);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  if (!tip) return null;
  const ring = pos?.ring;
  // a control bigger than a button gets a ring just inside it; a small one a ring round it
  const big = ring && ring.width > 200 && ring.height > 120;
  return (
    <>
      {ring && (
        <div
          className={`tip-ring${big ? " is-area" : ""}`}
          aria-hidden
          style={big ? { left: ring.left + 4, top: ring.top + 4, width: ring.width - 8, height: ring.height - 8 } : { left: ring.left - 4, top: ring.top - 4, width: ring.width + 8, height: ring.height + 8 }}
        />
      )}
      <div
        ref={card}
        className={`tip-card island is-${pos?.side ?? "below"}${phone ? " is-phone" : ""}`}
        role="dialog"
        aria-label={`Tip ${at + 1} of ${list.length}: ${tip.title}`}
        data-tip-kind={kind}
        data-tip-step={at + 1}
        style={pos ? { left: pos.left, top: pos.top } : { left: -9999, top: -9999 }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <span className="tip-count">
          {at + 1} of {list.length}
        </span>
        <b className="tip-title">{tip.title}</b>
        <p className="tip-body">{phone || !tip.keys ? tip.body : `${tip.body} ${tip.keys}`}</p>
        <div className="tip-foot">
          <button className="tip-skip" data-tip-skip onClick={finish}>
            Skip
          </button>
          <button className="btn btn-sm btn-primary tip-next" data-tip-next onClick={next}>
            {at + 1 >= list.length ? "Done" : "Next"}
          </button>
        </div>
      </div>
    </>
  );
}
